import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Popover, PopoverContent, PopoverTrigger } from "@/clientWeb/components/ui/popover"
import { getInitials } from "@vc/services/format"
import { inviteeRows, type InviteesCardMeeting } from "./invitee-rows"

const HOVER_OPEN_MS = 250
const HOVER_CLOSE_MS = 200

/**
 * The dashboard row's avatar stack, as a way into the full guest list.
 *
 * The row shows three faces and "+N", which told nobody who was coming without
 * opening the meeting. Hovering the stack shows the list; clicking it keeps the
 * card open until you click elsewhere or press Escape — the guest-list card
 * calendar apps show for an event.
 *
 * Clicks inside never reach the row underneath: React carries events out of a
 * portal to its React parent, and the row's own click opens the meeting.
 */
export function InviteesCard({
  meeting,
  avatarColor,
  children,
}: {
  meeting: InviteesCardMeeting
  avatarColor: (seed: string) => string
  children: ReactNode
}) {
  const t = useTranslation()
  const rows = useMemo(() => inviteeRows(meeting), [meeting])
  const inviteeCount = rows.length - 1
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }
  useEffect(() => clearTimer, [])

  const hoverOpen = () => {
    clearTimer()
    timer.current = setTimeout(() => setOpen(true), HOVER_OPEN_MS)
  }
  const hoverClose = () => {
    clearTimer()
    if (!pinned) timer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        clearTimer()
        setOpen(next)
        if (!next) setPinned(false)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("meetings.inviteesCount", { n: inviteeCount })}
          className="flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          onClick={(e) => {
            // Not to the row (it opens the meeting), and not to Radix's own
            // toggle: open/pinned are decided here.
            e.stopPropagation()
            e.preventDefault()
            clearTimer()
            // A click on a card that hover opened keeps it open instead of
            // closing it under the pointer.
            if (open && !pinned) {
              setPinned(true)
              return
            }
            setPinned(!open)
            setOpen(!open)
          }}
          onMouseEnter={hoverOpen}
          onMouseLeave={hoverClose}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 bg-card text-foreground"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={clearTimer}
        onMouseLeave={hoverClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-baseline justify-between px-4 pt-3 pb-2 border-b border-border">
          <p className="text-sm font-semibold">{t("meetings.inviteesLabel")}</p>
          <p className="text-xs text-muted-foreground">{t("meetings.inviteesCount", { n: inviteeCount })}</p>
        </div>
        <ul className="max-h-72 overflow-y-auto py-1" aria-label={t("meetings.inviteesLabel")}>
          {rows.map((r) => (
            <li key={r.key} className="flex items-center gap-3 px-4 py-2">
              <div
                className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold"
                style={{ background: avatarColor(r.email ?? r.name), color: "rgba(0,0,0,0.55)" }}
                aria-hidden
              >
                {getInitials(r.name) || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{r.name}</p>
                {r.email && r.email !== r.name && (
                  <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                )}
              </div>
              {r.kind !== "invitee" && (
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {r.kind === "organizer" ? t("common.host") : t("meetings.viewOnlyBadge")}
                </span>
              )}
            </li>
          ))}
          {inviteeCount === 0 && (
            <li className="px-4 py-2 text-xs text-muted-foreground">{t("meetings.noInvitees")}</li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
